import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import { Line } from 'react-chartjs-2';
import ChartDataDrag from 'chartjs-plugin-dragdata';
import { useGesture } from '@use-gesture/react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataDrag,
    annotationPlugin
)

const CarbonDioxieChart = ({ chartFilterOptions, settingsUpdateToggle }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [seedData, setSeedData] = useState([{
        labels: [],
        datasets: [],
    }]);
    const [parameters, setParameters] = useState({
        binId: 0,
        deviceId: "All",
        dateFrom: new Date(),
        dateTo: new Date(),
        hourFrom: 0,
        hourTo: 0,
        settingsUpdateToggle: false,
    });
    const chartRef = useRef(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    const gesture = useGesture({
        onDragEnd: e => {
            if (chartRef && chartRef.current) {
                chartRef.current.tooltip?.setActiveElements([]);
            }
        },
    }, []);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const options = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
        padding: {
            top: 0,
            right: 0,
            bottom: 0, 
            left: 0, 
        },
        margin: {
            right: 0
        },
        minWidth: 0,
    },
    interaction: {
        mode: 'nearest',
        intersect: false,
        axis: "xy",
    },
    scales: {
        x: {
            dragData: false,
            grid: {
                color: isLight ? "rgba(51, 51, 51, 0.33)" : "rgb(255, 193, 78, 0.33)"
            },
            ticks: {
                color: isLight ? "#333333" : "white",
                minRotation: 90,
                maxRotation: 90,
                font: {
                    size: 14,
                }
            },
            display: true,
            autoSkip: false,
        },
        y: {
            dragData: false,
            beginAtZero: false,
            //min: 200,
            //max: 1400,
            stepSize: 500,
            grid: {
                color: isLight ? "rgba(51, 51, 51, 0.33)" : "rgb(255, 193, 78, 0.33)"
            },
            ticks: {
                color: isLight ? "#333333" : "white",
                font: {
                    size: 14,
                }
            },
        }
    },
    plugins: {
        legend: {
            display: true,
            position: "bottom",
            labels: {
                font: {
                    size: 14,
                },
                color: isLight ? '#333333' : 'white',
            },
        },
        tooltips: {
            enabled: false,
            titleColor: isLight ? "rgb(139, 0, 0)" : "red",
        },
        title: {
            display: true,
            color: isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)",
            align: "center",
            fontSize: 40,
            position: "bottom",
            padding: {
                top: 10,
            }
        },
        annotation: {
            annotations: [],
        },
    },
    };
    }, [forceUpdate]);

    let binId = chartFilterOptions.binId;
    let deviceId = chartFilterOptions.deviceId;
    let dateFrom = chartFilterOptions.dateFrom;
    let dateTo = chartFilterOptions.dateTo;
    let hourFrom = chartFilterOptions.hourFrom;
    let hourTo = chartFilterOptions.hourTo;

    useEffect(() => {
        if (binId === null) {
            return;
        }

        // if (parameters.binId === binId &&
        //     parameters.deviceId === deviceId &&
        //     parameters.dateFrom === dateFrom &&
        //     parameters.dateTo === dateTo &&
        //     parameters.hourFrom === hourFrom &&
        //     parameters.hourTo === hourTo &&
        //     parameters.settingsUpdateToggle === settingsUpdateToggle) { 
        //     return;
        // }
        // else {
        //     parameters.binId = binId;
        //     parameters.deviceId = deviceId;
        //     parameters.dateFrom = dateFrom;
        //     parameters.dateTo = dateTo;
        //     parameters.hourFrom = hourFrom;
        //     parameters.hourTo = hourTo;
        //     parameters.settingsUpdateToggle = settingsUpdateToggle;
        // }

        (async () => {
            await fetchWeatherStationReadings();
        })();
    }, [chartFilterOptions]);

    const fetchWeatherStationReadings = async () => {
        setIsLoading(true);

        try {
        let carbonDioxideChartFilters = window.document.getElementsByClassName("carbonDioxideChartFilter");
        if (carbonDioxideChartFilters && carbonDioxideChartFilters.length > 0) {
            let carbonDioxideChartFilter = carbonDioxideChartFilters[0];
            if (carbonDioxideChartFilter) {
                carbonDioxideChartFilter.style.display = "none";
                carbonDioxideChartFilter.childNodes[0].style.color = global.gw.theme === 'light' ? "#33333361" : "#f4cc8e61";
            }
        }

        if (!binId || binId == 0) {
            binId = null;
        }

        let jsonSettings = {
            deviceId: deviceId == "All" ? null : deviceId,
            readingTypeId: null,
            binId: binId,
        };

        options.plugins.annotation.annotations = [];

        let weatherStationReadingSettingsResponse = await createApiEndpoint().fetchWeatherStationReadingSettings("direct", jsonSettings);

        let weatherStationReadingSettings = null;
        if (weatherStationReadingSettingsResponse && weatherStationReadingSettingsResponse.data && weatherStationReadingSettingsResponse.data.getWeatherStationReadingSettings) {
            let arrayBuffer = global.base64StringToArrayBuffer(weatherStationReadingSettingsResponse.data.getWeatherStationReadingSettings);
            let decompressed = await global.decompress(arrayBuffer, "gzip");
            weatherStationReadingSettings = JSON.parse(decompressed);
        }

        if (!weatherStationReadingSettings) {
            setIsLoading(false);
            return;
        }

        if (!binId || binId == 0) {
            binId = null;
        }

        if (!hourFrom || hourFrom < 0) {
            hourFrom = 0;
        }
        else if (hourFrom > 23) {
            hourFrom = 23;
        }

        let hourFromString = `${hourFrom}`;
        if (hourFrom.toString().length === 1) {
            hourFromString = `0${hourFrom}`;
        }

        if (!hourTo || hourTo < 0) {
            hourTo = 0;
        }
        else if (hourTo > 23) {
            hourTo = 23;
        }

        let hourToString = `${hourTo}`;
        if (hourTo.toString().length === 1) {
            hourToString = `0${hourTo}`;
        }

        let jsonReadings = {
            binId: binId,
            deviceId: deviceId == "All" ? null : deviceId,
            dateFrom: dateFrom.toJSON().split("T")[0] + `T${hourFromString}:00:00.000Z`,
            dateTo: global.dateToJsonCurrentTimeZone(dateTo), //.split("T")[0] + `T${hourToString}:59:59.999Z`
        };

        let siteWeatherStationReadings = await createApiEndpoint().fetchSiteWeatherStationReadings("direct", jsonReadings);

        if (!siteWeatherStationReadings?.data?.getSiteWeatherStationReadings?.length) {
            setSeedData([{
                labels: ["No Data"],
                datasets: [{
                    label: "Carbon Dioxide",
                    data: [0],
                    backgroundColor: global.gw.theme === 'light' ? "#e0e0e0" : "#2d2d2d",
                    borderColor: global.gw.theme === 'light' ? "#e0e0e0" : "#2d2d2d",
                    borderWidth: 1,
                    fill: false,
                }],
            }]);
            setIsLoading(false);
            return;
        }
        
        let siteBinCurrentWeatherStationReadings = global.gw.currentWeatherReadings.filter(x => x.siteId === global.gw.selectedSite.siteGuid && ((x.binId === binId && (deviceId === "All" || deviceId === null)) || (binId === null && (deviceId === "All" || deviceId === null))));

        let generatedDaysGroup = [];
        let hourlyDates = global.getDatesByHour(new Date(jsonReadings.dateFrom));
        let generatedDeviceReadings = [];
        for (var h = 0; h < hourlyDates.length; h++) {
            let hourlyDate = hourlyDates[h].toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");
            for (var d = 0; d < siteBinCurrentWeatherStationReadings.length; d++) {
                let deviceReading = siteBinCurrentWeatherStationReadings[d];
                generatedDeviceReadings.push({
                    BarometricPressure: deviceReading.BarometricPressure,
                    CarbonDioxide: deviceReading.CarbonDioxide,
                    DateRead: hourlyDate,
                    DeviceId: deviceReading.DeviceId,
                    DewPoint: deviceReading.DewPoint,
                    Emc: deviceReading.Emc,
                    Humidity: deviceReading.Humidity,
                    Mc: deviceReading.Mc,
                    StaticPressure: deviceReading.StaticPressure,
                    Temperature: deviceReading.Temperature,
                    WeatherStationDeviceId: deviceReading.WeatherStationDeviceId,
                    WeatherStationReadingId: deviceReading.WeatherStationReadingId,
                    RealDateRead: deviceReading.DateRead,
                });
            }
        }
        generatedDaysGroup = Object.groupBy(generatedDeviceReadings, ({ DateRead }) => DateRead);

        let daysGroup = [];
        let deviceReadings = [];
        if (siteWeatherStationReadings && siteWeatherStationReadings.data && siteWeatherStationReadings.data.getSiteWeatherStationReadings) {
            let arrayBuffer = global.base64StringToArrayBuffer(siteWeatherStationReadings.data.getSiteWeatherStationReadings);
            let decompressed = await global.decompress(arrayBuffer, "gzip");
            deviceReadings = JSON.parse(decompressed);

            let addedDeviceReadings = [];
            for (var d = 0; d < siteBinCurrentWeatherStationReadings.length; d++) {
                if (!deviceReadings.some(x => x.DeviceId === siteBinCurrentWeatherStationReadings[d].DeviceId)) {
                    for (var dr = 0; dr < deviceReadings.length; dr++) {
                        let deviceReading = deviceReadings[dr];
                        let generatedDeviceReading = generatedDeviceReadings.filter(x => x.DeviceId === siteBinCurrentWeatherStationReadings[d].DeviceId)[0];
                        addedDeviceReadings.push({
                            BarometricPressure: generatedDeviceReading.BarometricPressure,
                            CarbonDioxide: generatedDeviceReading.CarbonDioxide,
                            DateRead: deviceReading.DateRead,
                            DeviceId: generatedDeviceReading.DeviceId,
                            DewPoint: generatedDeviceReading.DewPoint,
                            Emc: generatedDeviceReading.Emc,
                            Humidity: generatedDeviceReading.Humidity,
                            Mc: generatedDeviceReading.Mc,
                            StaticPressure: generatedDeviceReading.StaticPressure,
                            Temperature: generatedDeviceReading.Temperature,
                            WeatherStationDeviceId: generatedDeviceReading.WeatherStationDeviceId,
                            WeatherStationReadingId: generatedDeviceReading.WeatherStationReadingId,
                            RealDateRead: generatedDeviceReading.DateRead,
                        });
                    }
                }
            }

            for (var i = 0; i < addedDeviceReadings.length; i++) {
                deviceReadings.push(addedDeviceReadings[i]);
            }

            daysGroup = Object.groupBy(deviceReadings, ({ DateRead }) => new Date(DateRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", ""));
        }
        else {
            deviceReadings = generatedDeviceReadings;
            daysGroup = generatedDaysGroup;
        }

        let labels = [];
        let datasets = [];

        if (siteWeatherStationReadings && siteWeatherStationReadings.data && siteWeatherStationReadings.data.getSiteWeatherStationReadings) {
            labels = Object.keys(daysGroup);

            const deviceIdGroup = Object.groupBy(deviceReadings, ({ DeviceId }) => DeviceId);
            const deviceIdList = Object.keys(deviceIdGroup);

            for (let l = 0; l < deviceIdList.length; l++) {
                let deviceId = deviceIdList[l];
                let binName = weatherStationReadingSettings.filter(x => x.DeviceId == deviceId)[0]?.BinName;
                let dataSet = {
                    label: `Bin: ${binName}\r\nDevice: ${deviceId}`,
                    data: [],
                    tension: 0.5,
                    binId: parameters.binid,
                    fill: false,
                    spanGaps: true,
                    pointRadius: 1,
                };
                datasets.push(dataSet);

                let sg = deviceIdGroup[deviceId];

                let mappedNumbers = deviceReadings.filter(x => x.DeviceId == deviceId).map(x => +x.CarbonDioxide);
                let maxCarbonDioxide = Math.max.apply(Math, mappedNumbers);

                let color = "rgb(112, 255, 0)";

                let isNotRead = sg.some(x => x.RealDateRead);
                if (isNotRead) {
                    color = "rgb(255, 0, 0)";
                    dataSet.label += ` ${sg[0].RealDateRead} `;
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.HighWarning &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.ModerateLimit) {
                    color = "#ffff00";
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.ModerateLimit &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.HighLimit) {
                    color = "#ffa500";
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.HighLimit &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.CriticalLimit) {
                    color = "#ff4500";
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.CriticalLimit) {
                    color = "#d32f2f";
                }

                dataSet.borderColor = color;
                dataSet.backgroundColor = dataSet.borderColor;

                for (let i = 0; i < labels.length; i++) {
                    let day = labels[i];
                    let ths = sg.filter(x => new Date(x.DateRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "") === day);

                    if (ths && ths.length > 0 && ths[0].CarbonDioxide > -40) {
                        dataSet.data.push(ths[0].CarbonDioxide);
                    }
                    else {
                        dataSet.data.push(null);
                    }
                }
            }

            let tempSeedData = seedData;
            tempSeedData = [{
                labels: labels,
                datasets: datasets,
            }];
            setSeedData(tempSeedData);
            setIsLoading(false);
        }
        else {
            setIsLoading(false);
        }
        } catch (error) {
            console.error("CarbonDioxideChart: failed to load readings", error);
            setSeedData([{
                labels: ["No Data"],
                datasets: [{
                    label: "Carbon Dioxide",
                    data: [0],
                    backgroundColor: global.gw.theme === 'light' ? "#e0e0e0" : "#2d2d2d",
                    borderColor: global.gw.theme === 'light' ? "#e0e0e0" : "#2d2d2d",
                    borderWidth: 1,
                    fill: false,
                }],
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <MuiMaterials.Box
                key={`co2-chart-spinner-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    fontSize: "50px",
                    color: getThemeColor('#333333', 'rgb(244, 204, 142)'),
                    margin: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    right: "0",
                    left: "1%",
                    top: "0",
                    bottom: "0",
                    background: getThemeColor('rgba(255, 255, 255, 0.9)', 'rgba(0, 0, 0, 0.8)'),
                }}
            >
                <MuiMaterials.RotatingLines
                    strokeColor={getThemeColor('#333333', '#f4cc8e')}
                    strokeWidth="3"
                    animationDuration="0.72"
                    width="48"
                    visible={true}
                />
            </MuiMaterials.Box>
        );
    }

    return (
        <MuiMaterials.Box
            sx={{
                height: "100%",
            }}
        >
            <MuiMaterials.Typography
                component="div"
                sx={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    fontWeight: 400,
                    color: getThemeColor('#666666', '#a8abb1'),
                    textAlign: "left",
                    mb: 1,
                }}
            >
                If you would like to have CO<sub>2</sub> meters installed on any of your bins call us at (662) 838-3698.
            </MuiMaterials.Typography>
            <MuiMaterials.Box
                className="carbonDioxideChart"
                sx={{
                    height: "76% !important",
                    marginTop: "15px",
                    position: "relative",
                }}
            >
                {
                    seedData[0].datasets.length === 0 ? 
                    null
                : 
                    <Line
                        key={`co2-chart-${global.gw.theme}-${forceUpdate}`}
                        className={"carbonDioxideLine"}
                        data={seedData[0]}
                        options={options}
                        ref={chartRef}
                        onMouseOut={(event) => {
                            if (chartRef && chartRef.current) {
                                chartRef.current.tooltip?.setActiveElements([]);
                            }
                        }}
                        onMouseLeave={(event) => {
                            if (chartRef && chartRef.current) {
                                chartRef.current.tooltip?.setActiveElements([]);
                            }
                        }}
                        {...gesture()}
                    />
                }
                
                {isLoading ? <MuiMaterials.Box
                    key={`co2-chart-spinner-overlay-${global.gw.theme}-${forceUpdate}`}
                    sx={{
                        fontSize: "50px",
                        color: getThemeColor('#333333', 'rgb(244, 204, 142)'),
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "absolute",
                        right: "0",
                        left: "1%",
                        top: "0",
                        bottom: "0",
                        background: getThemeColor('rgba(255, 255, 255, 0.9)', 'rgba(0, 0, 0, 0.8)'),
                    }}
                >
                    <MuiMaterials.RotatingLines
                        strokeColor={getThemeColor('#333333', '#f4cc8e')}
                        strokeWidth="3"
                        animationDuration="0.72"
                        width="48"
                        visible={true}
                    />
                </MuiMaterials.Box> : null}

            </MuiMaterials.Box>
            </MuiMaterials.Box>
       
    );
}
export default CarbonDioxieChart;